const API_BASE = "/api";

function getToken() {
  return localStorage.getItem("dashboard_token");
}

export function setToken(token) {
  localStorage.setItem("dashboard_token", token);
}

export function clearToken() {
  localStorage.removeItem("dashboard_token");
}

function authHeader() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function guard(res) {
  if (res.status === 401) {
    clearToken();
    window.location.href = "/login";
    throw new Error("Unauthorized");
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Request failed" }));
    // The body rides along for callers that act on it (a taken email's holder).
    throw Object.assign(new Error(err.error || "Request failed"), { status: res.status, body: err });
  }

  return res;
}

async function unwrap(res) {
  return (await guard(res)).json();
}

export async function api(path, options = {}) {
  const headers = { "Content-Type": "application/json", ...authHeader(), ...options.headers };
  return unwrap(await fetch(`${API_BASE}${path}`, { ...options, headers }));
}

// Raw binary POST — DM screenshots go up as image bytes with the file's own
// content type, not base64 inside JSON, so the JSON body limit doesn't apply.
export async function apiUpload(path, file) {
  return unwrap(await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": file.type, ...authHeader() },
    body: file,
  }));
}

// Raw binary GET — stored attachments come back as image bytes from an
// auth-gated endpoint, so a timeline thumbnail can't just be an <img src>: the
// bytes are fetched with the bearer token and rendered from an object URL.
export async function apiBlob(path) {
  const res = await guard(await fetch(`${API_BASE}${path}`, { headers: authHeader() }));
  return res.blob();
}

/**
 * A generated document: the bytes, plus what the server said ABOUT them.
 *
 * The agreement endpoint's body is the .docx itself, so its warnings and its
 * fee summary ride back in `X-Agreement-*` headers — which is the only place
 * they can go, and they must go somewhere: a document that went out with
 * blanks nobody was told about is the failure the whole card exists to prevent.
 *
 * Returned as an object URL rather than saved straight to disk. The card shows
 * the filename, the blanks and the terms used first, and downloading is a click
 * from there — the browser cannot be trusted to have shown any of that if the
 * file lands in ~/Downloads the instant the button is pressed.
 */
export async function apiFile(path, options = {}) {
  const headers = { "Content-Type": "application/json", ...authHeader(), ...options.headers };
  const res = await guard(await fetch(`${API_BASE}${path}`, { ...options, headers }));
  const disposition = res.headers.get("Content-Disposition") || "";
  const named = /filename="?([^"]+)"?/.exec(disposition);
  const read = (header) => {
    try {
      return JSON.parse(res.headers.get(header) || "null");
    } catch {
      return null;
    }
  };
  return {
    url: URL.createObjectURL(await res.blob()),
    filename: named ? named[1] : "agreement.docx",
    warnings: read("X-Agreement-Warnings") || [],
    summary: read("X-Agreement-Summary") || {},
  };
}

export function getSSEUrl() {
  const token = getToken();
  return `${API_BASE}/sse?token=${token}`;
}
